/**
 * SALES-FORECASTING MODULE
 * Self-contained. Import only from /shared or within this module.
 *
 * dashboardSummary — this module's operations-hub (Home) contribution: a
 * "Forecast" KPI summing planned case_forecast over the next 30 / 60 / 90 days
 * (user-toggleable on the card). Forecasts are stored monthly, so a window is
 * pro-rated across the calendar months it spans.
 */

import { loadForecastData } from "./forecastService.js";

// Cases forecast over the next `days` calendar days, pro-rating each month's
// total by the fraction of that month inside the window.
function windowForecast(monthTotals, days, now) {
  const DAY = 86400000;
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const end = new Date(start.getTime() + days * DAY); // exclusive
  let total = 0;
  let cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  while (cursor < end) {
    const y = cursor.getFullYear(), m = cursor.getMonth() + 1;
    const monthStart = new Date(y, m - 1, 1);
    const monthEnd = new Date(y, m, 1); // exclusive
    const daysInMonth = (monthEnd - monthStart) / DAY;
    const ovStart = Math.max(start.getTime(), monthStart.getTime());
    const ovEnd = Math.min(end.getTime(), monthEnd.getTime());
    const ovDays = Math.max(0, (ovEnd - ovStart) / DAY);
    total += (monthTotals[`${y}-${m}`] || 0) * (ovDays / daysInMonth);
    cursor = new Date(y, m, 1); // advance one month
  }
  return total;
}

export async function getDashboardSummary() {
  let data;
  try { data = await loadForecastData(); } catch { return { kpis: [], signals: [] }; }

  const monthTotals = {};
  for (const row of data.rawForecast || []) {
    const y = Number(row.fields?.Year);
    const m = Number(row.fields?.Month);
    const v = Number(row.fields?.CaseForecast) || 0;
    if (!y || !m) continue;
    monthTotals[`${y}-${m}`] = (monthTotals[`${y}-${m}`] || 0) + v;
  }

  const now = new Date();
  const variants = [30, 60, 90].map((d) => ({
    key: String(d),
    label: `${d}d`,
    display: Math.round(windowForecast(monthTotals, d, now)).toLocaleString(),
    unit: "cases",
    sub: `forecast · next ${d} days`,
    status: "neutral",
  }));

  // Only show the KPI when there's a forward-looking forecast to sum.
  if (!variants.some((v) => Number(v.display.replace(/,/g, "")) > 0)) return { kpis: [], signals: [] };

  return {
    kpis: [{
      key: "forecast-window", label: "Forecast", to: "/forecasting",
      storageKey: "bc.dash.forecastWindow", defaultVariant: "30", variants,
    }],
    signals: [],
  };
}
